import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { UserCheck, Send, MousePointerClick, Star, Link2, Settings2, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import { useCelebration } from '@/hooks/useCelebration';
import KanbanBoard, { StatStrip, CardTag, timeAgo } from '@/components/training/KanbanBoard';
import LeadDetailDrawer from "@/components/training/LeadDetailDrawer";

const ReviewsPage = () => {
  const { user, selectedBusiness } = useAuth();
  const { celebrate } = useCelebration();
  const canEdit = ['admin', 'super_admin'].includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [drawerLeadId, setDrawerLeadId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [form, setForm] = useState({ google_review_url: '', review_no_response_days: 14 });
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/training/reviews/board`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include',
      });
      if (res.ok) setData(await res.json());
      else toast.error('Failed to load review requests');
    } catch {
      toast.error('Failed to load review requests');
    } finally {
      setLoading(false);
    }
  }, [token, selectedBusiness?.business_id]);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  const askForReview = async (card) => {
    setBusyId(card.id);
    try {
      const res = await fetch(`${API_URL}/api/training/reviews/request`, {
        method: 'POST', headers: authHeaders, credentials: 'include',
        body: JSON.stringify({ lead_id: card.lead_id, course_id: card.course_id }),
      });
      if (res.ok) { toast.success(`Review request sent to ${card.name}`); fetchBoard(); }
      else toast.error('Failed to send review request');
    } finally {
      setBusyId(null);
    }
  };

  const setStatus = async (card, status) => {
    setBusyId(card.id);
    try {
      const res = await fetch(`${API_URL}/api/training/reviews/${card.id}`, {
        method: 'PUT', headers: authHeaders, credentials: 'include', body: JSON.stringify({ status }),
      });
      if (res.ok) {
        if (status === 'review_received') { celebrate(); toast.success('Review confirmed!'); }
        fetchBoard();
      } else toast.error('Failed to update');
    } finally {
      setBusyId(null);
    }
  };

  const openSettings = () => {
    setForm({
      google_review_url: data?.settings?.google_review_url || '',
      review_no_response_days: data?.settings?.no_response_days || 14,
    });
    setSettingsOpen(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/training/growth-settings`, {
        method: 'PUT', headers: authHeaders, credentials: 'include',
        body: JSON.stringify({ ...form, review_no_response_days: parseInt(form.review_no_response_days, 10) || 14 }),
      });
      if (res.ok) { toast.success('Saved'); setSettingsOpen(false); fetchBoard(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Failed to save'); }
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 text-pink-400 animate-spin" /></div>;
  }

  const settings = data?.settings || {};
  const stats = data?.stats || {};
  const cols = data?.columns || {};

  const baseCard = (c, extra) => (
    <div>
      <button type="button" onClick={() => setDrawerLeadId(c.lead_id)} className="text-sm font-semibold text-slate-900 truncate hover:text-pink-600 text-left block max-w-full">{c.name}</button>
      {c.course && <p className="text-xs text-slate-500 truncate">{c.course}</p>}
      {extra}
    </div>
  );

  const confirmButton = (c) => canEdit && (
    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-green-700" disabled={busyId === c.id} onClick={() => setStatus(c, 'review_received')}>
      <Star className="w-3 h-3 mr-1" /> Got review
    </Button>
  );

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Review requests</h1>
          <p className="text-slate-500 mt-1">Which finished students have been asked for a Google review, and what happened next.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-xl h-9" onClick={fetchBoard} aria-label="Refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {canEdit && (
            <Button variant="outline" className="rounded-xl h-9" onClick={openSettings}>
              <Settings2 className="w-4 h-4 mr-1" /> Review settings
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
        <Link2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-400" />
        <p>
          {!settings.has_review_workflow
            ? 'No GHL review workflow is connected yet, so requests are logged but not sent. It is set up in Settings → GHL.'
            : !settings.google_review_url
              ? 'Add your Google review page in Review settings so the tracked link knows where to send people.'
              : 'Each request carries a tracked link. When a student taps it, they move to "Link clicked" automatically.'}
        </p>
      </div>

      <StatStrip items={[
        { label: 'Waiting to be asked', icon: UserCheck, value: stats.eligible ?? 0 },
        { label: 'Asked', icon: Send, value: stats.asked ?? 0 },
        { label: 'Clicked the link', icon: MousePointerClick, value: stats.link_clicked ?? 0 },
        { label: 'Reviews confirmed', icon: Star, value: stats.confirmed ?? 0 },
      ]} />

      <KanbanBoard columns={[
        {
          key: 'eligible', title: 'Eligible', tone: 'slate', cards: cols.eligible || [], count: stats.eligible,
          emptyText: 'Finished students not asked yet',
          renderCard: (c) => baseCard(c, (
            <div className="flex items-center justify-between mt-2">
              <span className="text-[11px] text-slate-400">Finished {timeAgo(c.date)}</span>
              {canEdit && (
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" disabled={busyId === c.id} onClick={() => askForReview(c)}>
                  <Send className="w-3 h-3 mr-1" /> Ask
                </Button>
              )}
            </div>
          )),
        },
        {
          key: 'asked', title: 'Asked', tone: 'blue', cards: cols.asked || [], count: stats.asked,
          renderCard: (c) => baseCard(c, (
            <div className="flex items-center justify-between mt-2">
              <span className="text-[11px] text-slate-400">Asked {timeAgo(c.date)}</span>
              {confirmButton(c)}
            </div>
          )),
        },
        {
          key: 'link_clicked', title: 'Link clicked', tone: 'violet', cards: cols.link_clicked || [], count: stats.link_clicked,
          renderCard: (c) => baseCard(c, (
            <>
              <div className="mt-2"><CardTag tone="violet">Tracked link{c.click_count > 1 ? ` · ${c.click_count} clicks` : ''}</CardTag></div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] text-slate-400">Clicked {timeAgo(c.clicked_at)}</span>
                {confirmButton(c)}
              </div>
            </>
          )),
        },
        {
          key: 'confirmed', title: 'Review confirmed', tone: 'green', cards: cols.confirmed || [], count: stats.confirmed,
          renderCard: (c) => baseCard(c, <p className="text-[11px] text-slate-400 mt-2">{timeAgo(c.date)}</p>),
        },
        {
          key: 'no_response', title: 'No response', tone: 'amber', cards: cols.no_response || [], count: stats.no_response,
          subtitle: `No click after ${settings.no_response_days || 14} days`,
          renderCard: (c) => baseCard(c, (
            <div className="flex items-center justify-between mt-2">
              <span className="text-[11px] text-slate-400">Asked {timeAgo(c.date)}</span>
              {confirmButton(c)}
            </div>
          )),
        },
      ]} />

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Review settings</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Google review page</label>
              <Input value={form.google_review_url} onChange={(e) => setForm(p => ({ ...p, google_review_url: e.target.value }))} placeholder="https://g.page/r/.../review" className="rounded-xl font-mono text-xs" />
              <p className="text-xs text-slate-500 mt-1">
                In the GHL review message, use <code>{'{{inboundWebhookRequest.review_link}}'}</code> as the link. It records the click, then opens this page.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Move to "No response" after (days)</label>
              <Input type="number" min="1" max="90" value={form.review_no_response_days} onChange={(e) => setForm(p => ({ ...p, review_no_response_days: e.target.value }))} className="rounded-xl w-32" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button className="btn-primary rounded-xl" onClick={saveSettings} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <LeadDetailDrawer open={!!drawerLeadId} onOpenChange={(o) => { if (!o) setDrawerLeadId(null); }} leadId={drawerLeadId} onLeadUpdated={fetchBoard} />
    </div>
  );
};

export default ReviewsPage;
